import {useEffect, useState} from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Fade,
    Grid,
    IconButton,
    Tooltip,
    Typography,
    useMediaQuery
} from '@mui/material';
import {grey, orange} from '@mui/material/colors';
import CloseIcon from '@mui/icons-material/Close';
import {useQueryClient} from "@tanstack/react-query";
import {useCommonItems} from "../../../../../../../contexts/CommonItensProvider";
import {listaProdutos} from "../../../../products/configs/functions";
import {carregaLockers, manipulaLocker} from "../../../configs/functions";
import {
    buildLockerCells,
    buildPortasPayload,
    getDoorColors,
    getEffectiveDoorStatus,
    GROUP_BORDER,
    LOCKER_LEGEND,
    normalizePortasFromBackend
} from "../config/functions";
import {SetProductThirdStep} from "./TutorialLockerThirdStepPanel";

export function RestockingLockerDialog({open, onClose, locker, queryKey}) {
    const {exibirAlerta, exibirDialog, handleCloseDialog} = useCommonItems();
    const queryClient = useQueryClient();

    const isSmall = useMediaQuery(theme => theme.breakpoints.down('sm'));

    const [portas, setPortas] = useState([]);
    const [produtos, setProdutos] = useState([]);

    const [loadingProdutos, setLoadingProdutos] = useState(false);

    const [portaSelecionada, setPortaSelecionada] = useState(null);

    const [qtdColunas, setQtdColunas] = useState(Number(locker?.numeroColunas ?? locker?.numero_colunas ?? 1));

    const cols = Math.max(1, parseInt(qtdColunas) || 1);
    const sz = isSmall ? 36 : 48;
    const gap = 8;

    const loadProdutos = async id_empresa => {
        if (!id_empresa) {
            setProdutos([]);
            return;
        }

        setLoadingProdutos(true);

        try {
            const response = await listaProdutos({
                id_empresa,
                isFilter: true
            });

            setProdutos(response.data.data);
        } catch (error) {
            exibirAlerta('Erro ao carregar os Produtos', '', 'error');
        } finally {
            setLoadingProdutos(false);
        }
    };

    const loadConfigsLocker = async () => {
        if (!locker?.id) {
            return;
        }

        exibirDialog('Carregando configurações do locker...');

        try {
            const response = await carregaLockers({
                id: locker.id,
                locker_doors: true
            });

            const responseData = response?.data?.data;
            const armarioApi = Array.isArray(responseData) ? responseData[0] : responseData;

            if (!armarioApi) {
                throw new Error('Locker não encontrado');
            }

            const linhas = Number(armarioApi?.numeroLinhas ?? locker?.numeroLinhas ?? locker?.numero_linhas ?? 1);
            const colunas = Number(armarioApi?.numeroColunas ?? locker?.numeroColunas ?? locker?.numero_colunas ?? 1);
            const totalFisicas = linhas * colunas;

            setQtdColunas(colunas);

            const normalized = normalizePortasFromBackend({
                portasApi: armarioApi.portas ?? [],
                totalFisicas,
                cols: colunas,
                idArmario: armarioApi.id ?? locker.id,
            });

            setPortas(normalized);

            await loadProdutos(armarioApi.empresa?.id ?? locker.empresa?.id);
        } catch (error) {
            exibirAlerta('Erro ao carregar as configurações do locker', '', 'error');
        } finally {
            handleCloseDialog();
        }
    };

    useEffect(() => {
        if (open && locker?.id) {
            setPortaSelecionada(null);
            loadConfigsLocker();
        } else {
            setPortas([]);
            setProdutos([]);
            setPortaSelecionada(null);
        }
    }, [open, locker?.id]);

    const rows = portas.length > 0 ? Math.ceil(portas.length / cols) : 1;
    const cells = buildLockerCells(portas, cols);
    const portaSel = portaSelecionada !== null ? portas[portaSelecionada] : null;

    const applyToGroup = (gid, patch) => portas.map(porta => String(porta.groupId) === String(gid) ? {
        ...porta, ...patch,
        is_dirty: true
    } : porta);

    const applyToSingle = (idx, patch) => portas.map((porta, index) => index === idx ? {
        ...porta, ...patch,
        is_dirty: true
    } : porta);

    const handleClickPorta = cell => {
        const configDoor = cell.configDoor ?? cell.porta;

        if (getEffectiveDoorStatus(configDoor) === 'desativado') {
            exibirAlerta('Porta inativada', 'Esta porta está inativada e não pode ser reabastecida. Reative-a antes.', 'warning');
            return;
        }

        setPortaSelecionada(cell.physIdx);
    };

    const handleConfirm = ({ id_produto, id_produto_pai }) => {
        const portaAtual = portas[portaSelecionada];
        const gid = portaAtual?.groupId ?? null;

        const patch = {
            id_produto,
            id_produto_pai,
            qtd_produto: 0,
            mustSelectChild: false
        };

        if (gid !== null && gid !== undefined) {
            setPortas(applyToGroup(gid, {
                ...patch,
                doorStatus: 'ocupado'
            }));
        } else {
            setPortas(applyToSingle(portaSelecionada, {
                ...patch,
                status: 'ocupado'
            }));
        }

        setPortaSelecionada(null);
    };

    const handleRemove = () => {
        const portaAtual = portas[portaSelecionada];
        const gid = portaAtual?.groupId ?? null;

        const patch = {
            id_produto: null,
            id_produto_pai: null,
            qtd_produto: 0,
            mustSelectChild: false
        };

        if (gid !== null && gid !== undefined) {
            setPortas(applyToGroup(gid, {
                ...patch,
                doorStatus: 'livre'
            }));
        } else {
            setPortas(applyToSingle(portaSelecionada, {
                ...patch,
                status: 'livre'
            }));
        }

        setPortaSelecionada(null);
    };

    const handleSave = async () => {
        // Porta filha de grupo não tem produto próprio; ignora agrupadas na validação.
        const portaSemVariacao = portas.find(porta =>
            porta.status !== 'grouped' && !porta.id_produto && porta.mustSelectChild
        );

        if (portaSemVariacao) {
            exibirAlerta('Ops', 'Selecione a variação do produto para a porta ' + (portaSemVariacao.numero_logico ?? ''), 'error');
            return;
        }

        const portasPayload = buildPortasPayload(portas, cols);

        exibirDialog('Salvando. Aguarde...');

        try {
            const res = await manipulaLocker({
                id: locker.id,
                portas: portasPayload
            }, true);

            exibirAlerta('Sucesso', res.data.message, 'success');

            if (queryKey) {
                queryClient.invalidateQueries({
                    queryKey
                });
            }

            onClose();
        } catch (error) {
            const msg = error.response?.data?.error ?? error.response?.data?.message ?? 'Erro ao salvar reabastecimento';
            exibirAlerta('Erro', msg, 'error');
        } finally {
            handleCloseDialog();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}
            >
                Reabastecimento — {locker?.nome}

                <IconButton onClick={onClose}>
                    <CloseIcon/>
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <Grid
                    container
                    justifyContent="center"
                    alignItems="flex-start"
                    spacing={3}
                >
                    <Grid
                        item
                        xs={12}
                        md={7}
                    >
                        <Box
                            sx={{
                                border: `1px solid ${grey[300]}`,
                                borderRadius: 2,
                                p: 2
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: `repeat(${cols}, ${sz}px)`,
                                    gridTemplateRows: `repeat(${rows}, ${sz}px)`,
                                    gap: `${gap}px`,
                                    justifyContent: 'center',
                                }}
                            >
                                {cells.map(cell => {
                                    const { configDoor, physIdx, gridRow, gridCol, rowSpan, colSpan, grouped } = cell;
                                    const effStatus = getEffectiveDoorStatus(configDoor);
                                    const isSelected = physIdx === portaSelecionada;

                                    const { bg, hoverBg, border, color } = getDoorColors(configDoor);

                                    const tooltipTitle = effStatus === 'desativado'
                                        ? 'Porta inativada — reative-a antes de reabastecer.'
                                        : grouped
                                            ? (configDoor.id_produto ? 'Grupo com produto — clique para remover' : 'Clique para adicionar um produto ao grupo')
                                            : configDoor.id_produto
                                                ? 'Clique para remover o produto'
                                                : 'Clique para adicionar um produto';

                                    // Borda: selecionada -> laranja; agrupada -> tracejada; senão -> status.
                                    let borderStyle;
                                    if (isSelected) {
                                        borderStyle = `2.5px solid ${orange[600]}`;
                                    } else if (grouped) {
                                        borderStyle = GROUP_BORDER;
                                    } else {
                                        borderStyle = `1.5px solid ${border}`;
                                    }

                                    const btnSx = {
                                        gridColumn: `${gridCol} / span ${colSpan}`,
                                        gridRow: `${gridRow} / span ${rowSpan}`,
                                        width: `${colSpan * sz + (colSpan - 1) * gap}px`,
                                        height: `${rowSpan * sz + (rowSpan - 1) * gap}px`,
                                        minWidth: 0,
                                        p: 0,
                                        fontSize: 12,
                                        fontWeight: 500,
                                        borderRadius: 1.5,
                                        border: borderStyle,
                                        backgroundColor: isSelected ? orange[100] : bg,
                                        color: isSelected ? orange[900] : color,
                                        transform: isSelected ? 'scale(1.04)' : 'none',
                                        transition: 'all .15s',
                                        cursor: effStatus === 'desativado' ? 'not-allowed' : 'pointer',
                                        '&:hover': {
                                            backgroundColor: isSelected ? orange[200] : hoverBg,
                                            color: 'white'
                                        },
                                        '&.Mui-disabled': {
                                            backgroundColor: bg,
                                            color,
                                            opacity: 1,
                                            border: grouped ? GROUP_BORDER : `1.5px solid ${border}`,
                                        },
                                    };

                                    return (
                                        <Tooltip
                                            key={`${physIdx}-${configDoor.groupId ?? 'single'}`}
                                            title={tooltipTitle}
                                            placement="top"
                                        >
                                            <span
                                                style={{
                                                    gridColumn: `${gridCol} / span ${colSpan}`,
                                                    gridRow: `${gridRow} / span ${rowSpan}`
                                                }}
                                            >
                                                <Button
                                                    variant="contained"
                                                    disableElevation
                                                    disabled={effStatus === 'desativado'}
                                                    onClick={() => handleClickPorta(cell)}
                                                    sx={btnSx}
                                                >
                                                    {(!configDoor.numero_logico || effStatus === 'desativado') ? '—' : configDoor.numero_logico}
                                                </Button>
                                            </span>
                                        </Tooltip>);
                                })}
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                flexWrap: 'wrap',
                                gap: 2,
                                mt: 1.5
                            }}
                        >
                            {LOCKER_LEGEND.map(item => (<Box
                                key={item.label}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: .75
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 14,
                                        height: 14,
                                        borderRadius: '3px',
                                        backgroundColor: item.bg,
                                        border: item.dashed
                                            ? `2px dashed ${item.border}`
                                            : `1.5px solid ${item.border}`
                                    }}
                                />

                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    {item.label}
                                </Typography>
                            </Box>))}
                        </Box>

                        {portas.some(porta => getEffectiveDoorStatus(porta) === 'desativado') && (<Alert
                            severity="info"
                            sx={{ mt: 1.5 }}
                        >
                            Portas inativadas não podem ser reabastecidas por aqui.
                        </Alert>)}
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Fade in={portaSelecionada === null} unmountOnExit>
                            <Box
                                sx={{
                                    textAlign: 'center',
                                    color: 'text.secondary',
                                    mt: 4
                                }}
                            >
                                <Typography variant="body1">
                                    Selecione uma porta para adicionar ou remover um produto.
                                </Typography>
                            </Box>
                        </Fade>

                        <Fade  in={portaSelecionada !== null} unmountOnExit>
                            <Box>
                                {portaSel !== null && (
                                    <SetProductThirdStep
                                        porta={portaSel}
                                        produtos={produtos}
                                        loadingProdutos={loadingProdutos}
                                        onConfirm={handleConfirm}
                                        onRemove={handleRemove}
                                        onClose={() => setPortaSelecionada(null)}
                                        restockingMode
                                    />
                                )}

                                <Alert
                                    severity="info"
                                    sx={{ mt: 2 }}
                                >
                                    No reabastecimento, o estoque do produto é sempre definido como <strong>0</strong> e
                                    não pode ser alterado aqui. Você pode adicionar ou remover o produto da porta;
                                    o ajuste de quantidade é feito posteriormente através do processo de reabastecimento
                                    do locker.
                                </Alert>
                            </Box>
                        </Fade>
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions>
                <Button variant="outlined" onClick={onClose}>
                    Cancelar
                </Button>

                <Button variant="contained" onClick={handleSave}>
                    Salvar
                </Button>
            </DialogActions>
        </Dialog>);
}